import { Product } from "@/dataprocessor/types";
import { RecommendationProps } from "../recommendation";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { Eye } from "lucide-react";
import { createElementFromString } from "@/datastore/utils";
import { cloneNode } from "@/ui-sdk/ui-script/utils";

interface Props {
  complementaryStyles: RecommendationProps["complementaryStyles"];
  currentProductPosition: string;
  currentProductHandle: string;
}

export function ComplementaryStyleGrid({
  complementaryStyles,
  currentProductPosition,
  currentProductHandle,
}: Props) {
  const [selectedCategory, setSelectedCategory] = useState(
    Object.keys(complementaryStyles)[0],
  );

  return (
    <div className="hp-flex hp-flex-col hp-gap-4">
      <Tabs
        complementaryStyles={complementaryStyles}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <ComplementaryStyleGridItem
        complementaryStyles={complementaryStyles}
        currentProductPosition={currentProductPosition}
        currentProductHandle={currentProductHandle}
        styleName={selectedCategory}
        key={selectedCategory}
      />
    </div>
    // <Carousel className="" style={{}} disabled>
    //   <CarouselContent className="grid grid-cols-2">
    //     {Object.keys(complementaryStyles).map((style) => {
    //       return (
    //         <CarouselItem key={style} className="hp-h-full hp-basis-full">
    //           <ComplementaryStyleGridItem
    //             complementaryStyles={complementaryStyles}
    //             currentProductPosition={currentProductPosition}
    //             currentProductHandle={currentProductHandle}
    //             styleName={style}
    //           />
    //         </CarouselItem>
    //       );
    //     })}
    //   </CarouselContent>
    // </Carousel>
  );
}

function Tabs({
  complementaryStyles,
  selectedCategory,
  setSelectedCategory,
}: {
  complementaryStyles: RecommendationProps["complementaryStyles"];
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
}) {
  return (
    <ul className="no-scrollbar hp-flex hp-overflow-x-scroll hp-border-b">
      {Object.keys(complementaryStyles ?? {}).map((item) => (
        <button
          key={item}
          className={`hp-mr-1.5 hp-whitespace-nowrap hp-px-4 hp-py-2 hp-text-xl hp-font-semibold hp-uppercase ${
            selectedCategory === item
              ? "hp-border-b-2 hp-border-[#181818] hp-text-[#181818]"
              : "hp-border-b hp-text-[#A7A7A7]"
          }`}
          onClick={() => setSelectedCategory(item)}
        >
          {Object.keys(renameMapping).includes(item)
            ? renameMapping[item as keyof typeof renameMapping]
            : item}
        </button>
      ))}
    </ul>
  );
}

const renameMapping = {
  "Casual Streetwear": "Casual Street",
  "Athleisure Chic": "Sporty Chic",
  "Weekend Getaway": "Weekend Ready",
  "Sporty Luxe": "Sporty Luxe",
  "Urban Explorer": "City Explorer",
  "Bohemian Chic": "Boho Chic",
  "Traditional Elegance": "Classic Elegance",
  "Casual Street Style": "Easy Street",
  "Modern Fusion": "Modern Mix",
  "Ethnic Glam": "Ethnic Glam",
  "Casual Chic": "Casual Chic",
  "Sporty Adventure": "Active Style",
  "Bohemian Vibes": "Boho Vibes",
  "Urban Streetwear": "Urban Style",
  "Elegant Evening": "Evening Elegance",
  "Casual Comfort": "Everyday Comfort",
  "Resort Ready": "Resort Ready",
  "Cultural Fusion": "Mixed Heritage",
  "Chic Professional": "Work Chic",
  "Weekend Casual": "Weekend Casual",
  "Sporty Chic": "Rhythmic Grace",
  "Casual Summer Vibes": "Summer Casual",
  "Bohemian Festival Look": "Festival Style",
  "Layered Urban Style": "Layered Look",
  "Elegant Evening Out": "Night Out",
  "Sporty Streetwear": "Street Sport",
  "Smart Casual": "Smart Casual",
  "Edgy Urban": "Urban Edge",
  "Beach Ready": "Beachwear",
  "Casual Layering": "Layered Casual",
  "Street Style": "Street Style",
  "Festive Glam": "Festive Glam",
  "Modern Minimalist": "Modern Minimal",
  "Office Elegance": "Office Chic",
  "Casual Weekend": "Laidback Look",
  "Evening Glam": "Evening Glam",
  "Modern Minimalism": "Clean Look",
  "Sporty Fusion": "Sporty Mix",
  "Sporty Minimalism": "Simple Sport",
  "Outdoor Adventure": "Outdoor Ready",
  "Yoga Serenity": "Calm Wear",
  "High-Performance Athlete": "Performance Wear",
  "Luxe Loungewear": "Luxe Lounge",
  "Sporty Comfort": "Sporty Ease",
  "Athleisure Luxe": "Lush Leisure",
  "Outdoor Explorer": "Earthbound Odyssey",
  "Urban Fitness": "City Fit",
  "Retro Sport": "Retro Sport",
  "Edgy Street Style": "Street Edge",
  "Vintage Glam": "Vintage Glam",
  "Athleisure Vibe": "Everyday Sport",
  "Cozy Chic": "Cozy Chic",
  "Sporty Lounge": "Lounge Sport",
  "Casual Luxe": "Casual Luxe",
  "Weekend Vibes": "Weekend Mood",
  "Chic Homebody": "Home Chic",
  "Business Casual": "Business Casual",
  "Glamorous Night Out": "Night Glam",
  "Casual Ethnic": "Casual Ethnic",
  "Sporty Layered": "Layered Active",
  "Office Ready": "Office Ready",
  "Zen Retreat": "Zen Retreat",
  "Cozy Home Vibes": "Home Comfort",
  "Mindful Explorer": "Quiet Days",
  "Chic Spa Day": "Spa Day",
  "Nature's Embrace": "Nature Ready",
  "Sporty Casual": "Active Casual",
  "Weekend Relaxation": "Weekend Ease",
  "Sporty Elegance": "Sporty Elegance",
};

function ComplementaryStyleGridItem({
  complementaryStyles,
  currentProductPosition,
  currentProductHandle,
  styleName,
}: Props & {
  styleName: string;
}) {
  const [carouselMode, setCarouselMode] = useState<boolean>(false);
  const [productHtmls, setProductHtmls] = useState<string[]>([]);

  if (!styleName || !complementaryStyles[styleName]) {
    return null;
  }

  useEffect(() => {
    if (!carouselMode) return;

    async function renderProductCards() {
      const productHtmls = (
        await Promise.all(
          Object.values(complementaryStyles[styleName]).map((e) =>
            renderCloneNode(window.helium.datastore.memory.product[e].details),
          ),
        )
      )
        .map((e) => e?.__html)
        .filter(Boolean) as string[];

      setProductHtmls(productHtmls);
    }

    renderProductCards();
  }, [carouselMode]);

  if (carouselMode) {
    return (
      <div className="hp-w-screen">
        <div className="swimlane swimlane2">
          {productHtmls.map((e) => (
            <div dangerouslySetInnerHTML={{ __html: e }} />
          ))}
        </div>
      </div>
    );
  }

  // console.log(allSubCategories, "asddssdfsdfsddfs");

  return (
    <div className="md:hp-w-[400px] md:hp-max-w-[400px] hp-w-full hp-mx-auto hp-p-4 md:hp-0">
      <div>
        <img
          src={
            window.helium.datastore.memory.product?.[currentProductHandle]
              ?.details?.featuredImage.url
          }
          className="hp-w-full hp-h-[300px] hp-object-cover hp-my-4 hp-rounded-xl md:max-w-full"
        />
        <div
          className={cn(
            "hp-flex hp-whitespace-nowrap hp-overflow-scroll hp-gap-4 no-scrollbar",
            {
              "md:hp-flex md:hp-flex-start md:hp-gap-4 swimlane2": true,
            },
            "",
          )}
        >
          {Object.entries(
            complementaryStyles[styleName] as any as Record<string, string>,
          ).map(([subcategory, handle]) => {
            if (!window.helium.datastore.memory.product?.[handle]?.details) {
              console.info("TRIED FINDING ", handle, " complementaryStyles");
            }
            // window.helium.datastore.memory.product[currentProductHandle].details
            return (
              <CategoryGridItem
                product={window.helium.datastore.memory.product[handle].details}
                sideElement={false}
                subcategory={subcategory}
              />
            );
          })}
        </div>
      </div>
      <div
        onClick={() => setCarouselMode(true)}
        className="hp-mx-auto hp-mt-4 hp-cursor-pointer hover:hp-bg-gray-100 hp-w-fit hp-flex hp-items-center hp-gap-2 hp-rounded-full hp-border hp-border-gray-400 hp-px-3 hp-py-2"
      >
        <Eye className="hp-h-4 hp-w-4" />
        View style
      </div>
    </div>
  );
}

const renderCloneNode = async (item: Product) => {
  const templateNode = window.helium.productCard
    ? createElementFromString(window.helium.productCard)
    : window.helium.datastore.memory.html.product;
  const handle =
    window.helium.productCardHandle ||
    window.helium.datastore.memory.html.productHandle ||
    window.location.pathname.split("/").at(-1);

  if (!templateNode) return null;

  let existingProduct = (
    await window.helium.datastore.getProduct({
      id: handle ?? "",
    })
  )?.details;

  if (window.location.pathname.includes("product") && !existingProduct) {
    existingProduct = window.helium.datastore.memory.product["test"].details;
  }

  const node = cloneNode(templateNode, item, existingProduct);
  node.className += " !hp-w-[150px]";

  return { __html: node.outerHTML };
};

function CategoryGridItem({
  product,
  showImage = true,
  sideElement = false,
  subcategory,
}: {
  product: Product;
  showImage?: boolean;
  sideElement?: boolean;
  subcategory?: string;
}) {
  return (
    <div
      className={cn("hp-flex hp-h-full hp-flex-col hp-items-center hp-w-full", {
        "hp-col-span-1": sideElement,
        "hp-col-span-2": !sideElement,
      })}
    >
      {showImage && (
        <img
          className="hp-object-cover blur-scale-in hp hp-w-[120px] hp-h-[120px] hp-max-w-[120px] hp-rounded-xl"
          // height={100}
          // width={100}
          src={
            showImage
              ? (product?.featuredImage?.url ??
                product.images?.[0]?.url ??
                product?.image ??
                "")
              : ""
          }
        />
      )}
      <p className="hp-text-lg hp-gray-400 hp-capitalize hp-max-w-[120px] hp-text-wrap hp-text-center">
        {subcategory}
      </p>
    </div>
  );
}
